﻿import * as React from "react";
import { useState, useRef } from "react";
import {
  BatchLockUploadResponse,
  CsvParseResult,
  GroupChangePreview,
  ImportInventoryRequest,
  LockChangePreview,
  SyncChangesetPreview,
  UnitAcgMatchPreview,
} from "../../api/ApiClient";
import { apiClientFactory } from "../../helpers";
import { Combobox } from "components/ui/combobox";
import { Button as AppButton } from "components/ui/button";
import {
  Dialog as AppDialog,
  DialogContent as AppDialogContent,
  DialogDescription as AppDialogDescription,
  DialogFooter as AppDialogFooter,
  DialogHeader as AppDialogHeader,
  DialogTitle as AppDialogTitle,
} from "components/ui/dialog";
import { Alert as AppAlert } from "components/ui/alert";
import { Input as AppInput } from "components/ui/input";
import { Label as AppLabel } from "components/ui/label";
import {
  RadioGroup as AppRadioGroup,
  RadioGroupItem as AppRadioGroupItem,
} from "components/ui/radio-group";
import { Separator as AppSeparator } from "components/ui/separator";
import { Spinner as AppSpinner } from "components/ui/spinner";

export interface ImportModalProps {
  siteId: string;
  siteName: string;
  /** Semicolon-joined property external IDs (matches what the API returns on SiteResponse).
   *  When this contains a semicolon, the user must choose which property to use. */
  propertyExternalId?: string;
  onClose: () => void;
}

// â”€â”€ Visual helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ACTION_COLOR_CLASS: Record<string, string> = {
  Added: "text-emerald-800",
  Removed: "text-destructive",
  Updated: "text-amber-800",
};

const ActionBadge = ({ action }: { action: string }) => (
  <span
    className={`inline-block min-w-16 font-semibold ${ACTION_COLOR_CLASS[action] ?? "text-muted-foreground"}`}
  >
    {action}
  </span>
);

const COLLAPSE_THRESHOLD = 20;

const ChangeList = <T,>({
  label,
  items,
  renderItem,
}: {
  label: string;
  items: T[];
  renderItem: (item: T, i: number) => React.ReactNode;
}) => {
  const [showAll, setShowAll] = useState(false);
  if (!items.length) return null;
  const collapsed = !showAll && items.length > COLLAPSE_THRESHOLD;
  const visible = collapsed ? items.slice(0, COLLAPSE_THRESHOLD) : items;
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="mb-2 text-sm font-semibold">
        {label} ({items.length})
      </div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {visible.map((item, i) => renderItem(item, i))}
      </ul>
      {items.length > COLLAPSE_THRESHOLD && (
        <AppButton
          aria-expanded={showAll}
          onClick={() => setShowAll(!showAll)}
          className="mt-0.5 h-auto min-w-0 p-0 text-[0.82em]"
          size="sm"
          type="button"
          variant="link"
        >
          {showAll ? "Show less" : `Show all ${items.length}`}
        </AppButton>
      )}
    </div>
  );
};

// â”€â”€ Main component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
type Phase = "idle" | "parsing" | "parsed" | "parse-error" | "previewing" | "preview-ready" | "importing" | "done";

export const ImportModal: React.FC<ImportModalProps> = ({ siteId, siteName, propertyExternalId, onClose }) => {
  const propertyIds = propertyExternalId?.split(";").map(s => s.trim()).filter(Boolean) ?? [];
  const hasMultipleProperties = propertyIds.length > 1;
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(
    propertyIds.length === 1 ? propertyIds[0] : ""
  );
  // Property name is required for all imports; default to site name
  const [propertyNameOverride, setPropertyNameOverride] = useState(siteName);
  // Import mode
  const [additive, setAdditive] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [fileName, setFileName] = useState("");
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
  const [inventory, setInventory] = useState<ImportInventoryRequest | null>(null);
  const [preview, setPreview] = useState<SyncChangesetPreview | null>(null);
  const [importResult, setImportResult] = useState<BatchLockUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateOverrides = (): boolean => {
    if (hasMultipleProperties && !selectedPropertyId.trim()) {
      setError("Select or enter a property ID.");
      return false;
    }
    if (!propertyNameOverride.trim()) {
      setError("Property name is required.");
      return false;
    }
    return true;
  };

  const buildInventory = (base: ImportInventoryRequest): ImportInventoryRequest => ({
    ...base,
    propertyExternalId: selectedPropertyId.trim() || undefined,
    propertyNameOverride: propertyNameOverride.trim() || undefined,
    additive,
  });

  const executeImport = async () => {
    if (!inventory) return;
    setPhase("importing");
    setError(null);
    try {
      const data = await apiClientFactory().importInventory(siteId, buildInventory(inventory));
      setImportResult(data);
      setPhase("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed.");
      setPhase(preview ? "preview-ready" : "parsed");
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setFileName(file.name);
    setParseResult(null);
    setInventory(null);
    setPreview(null);
    setImportResult(null);
    setError(null);
    setPhase("parsing");
    try {
      const result = await apiClientFactory().parseCsv(siteId, file);
      setParseResult(result);
      setInventory(result.inventory);
      setPhase("parsed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "CSV parse failed.");
      setPhase("parse-error");
    }
  };

  const handlePreview = async () => {
    if (!inventory || !validateOverrides()) return;
    setPhase("previewing");
    setError(null);
    try {
      const data = await apiClientFactory().importPreview(siteId, buildInventory(inventory));
      setPreview(data);
      setPhase("preview-ready");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load preview.");
      setPhase("parsed");
    }
  };

  const handleImport = () => {
    if (!inventory || !validateOverrides()) return;
    if (!additive) { setConfirmOpen(true); return; }
    executeImport();
  };

  const hasChanges = preview && (
    preview.locks.length > 0 ||
    (preview.groups?.length ?? 0) > 0 ||
    (preview.unitAcgMatches?.length ?? 0) > 0
  );
  const canAct = ["parsed", "preview-ready"].includes(phase) && (parseResult?.inventory?.locks?.length ?? 0) > 0;

  const isInFlight = phase === "parsing" || phase === "previewing" || phase === "importing";

  return (
    <>
    <AppDialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isInFlight) {
          onClose();
        }
      }}
    >
      <AppDialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="max-h-[75vh] overflow-y-auto sm:max-w-2xl"
        onEscapeKeyDown={(event) => {
          if (isInFlight) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isInFlight) {
            event.preventDefault();
          }
        }}
      >
        <AppDialogHeader>
          <AppDialogTitle>Import Locks - {siteName}</AppDialogTitle>
        </AppDialogHeader>
        {/* File picker */}
        <div style={{ marginBottom: 16 }}>
          <input ref={fileInputRef} type="file" accept=".csv,.txt"
            style={{ display: "none" }} onChange={handleFileChange} />
          <AppButton size="sm" type="button" variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={phase === "parsing" || phase === "importing"}>
            {fileName ? "Change file" : "Choose CSV file"}
          </AppButton>
          {fileName && (
            <div className="mt-1.5 text-sm text-muted-foreground">
              <strong>{fileName}</strong>
              {parseResult && ` - ${parseResult.inventory.locks.length} lock${parseResult.inventory.locks.length !== 1 ? "s" : ""} parsed`}
              {parseResult?.headersDetected && (
                <span className="ml-0.75 text-emerald-800">Headers detected</span>
              )}
            </div>
          )}
        </div>

        {/* Column format reference */}
        <AppAlert className="mb-2" variant="info">
          <div className="mb-0.5 text-sm font-semibold">CSV format</div>
          <table style={{ fontSize: "0.8em", borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", paddingRight: 12, fontWeight: 600 }}>#</th>
                <th style={{ textAlign: "left", paddingRight: 12, fontWeight: 600 }}>Column</th>
                <th style={{ textAlign: "left", paddingRight: 12, fontWeight: 600 }}>Req</th>
                <th style={{ textAlign: "left", fontWeight: 600 }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "SystemLockId", "Yes", "Unique lock ID in the access control system"],
                ["2", "Name",         "Yes", "Display name for the lock"],
                ["3", "UnitNumber",   "No",  "Links lock to a unit ACG on import"],
                ["4", "DoorType",     "No",  "guest · common · door · commonDoor (default: door)"],
              ].map(([num, col, req, note]) => (
                <tr key={num}>
                  <td style={{ paddingRight: 12 }}>{num}</td>
                  <td style={{ paddingRight: 12 }}><code>{col}</code></td>
                  <td style={{ paddingRight: 12, fontWeight: req === "Yes" ? 600 : undefined }}>{req}</td>
                  <td>{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <span className="mt-0.5 block text-xs">
            A header row is optional - if the first row contains column names, columns can be in any order.
          </span>
        </AppAlert>

        {/* Import mode */}
        <div className={additive ? "mb-2" : "mb-1"}>
          <div className="mb-0.5 text-sm font-semibold">Import mode</div>
          <AppRadioGroup
            className="flex flex-wrap gap-4"
            value={String(additive)}
            onValueChange={(value) => setAdditive(value === "true")}
          >
            <div className="flex items-start gap-2">
              <AppRadioGroupItem id="import-mode-additive" value="true" />
              <AppLabel htmlFor="import-mode-additive">
                <span>
                  <span className="text-sm">Additive</span>
                  <span className="block text-xs text-muted-foreground">
                    Add or update; existing locks not in the file are kept
                  </span>
                </span>
              </AppLabel>
            </div>
            <div className="flex items-start gap-2">
              <AppRadioGroupItem id="import-mode-replace" value="false" />
              <AppLabel htmlFor="import-mode-replace">
                <span>
                  <span className={!additive ? "text-sm text-destructive" : "text-sm"}>
                    Replace
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Remove locks on this site that are not in the file
                  </span>
                </span>
              </AppLabel>
            </div>
          </AppRadioGroup>
        </div>

        {!additive && (
          <AppAlert className="mb-2" variant="destructive">
            <div className="text-sm font-semibold">Replacement mode is destructive</div>
            <div className="mt-0.5 text-sm">
              Locks currently on this site that are <strong>not in your CSV file</strong> will be permanently removed.
              The Preview Changes button will show exactly which locks would be affected before you commit.
            </div>
          </AppAlert>
        )}

        {/* Property and name overrides are always shown for multi-property sites. */}
        <div className="mb-2 rounded-md bg-muted p-3">
          {hasMultipleProperties && (
            <span className="mb-1 block text-xs text-muted-foreground">
              This site has multiple properties. Select which one to use for unit ACG operations.
            </span>
          )}
          <div className="mb-1.5">
            <label
              className="mb-1 block text-sm font-medium text-slate-700"
              htmlFor="import-property-id"
            >
              {hasMultipleProperties ? "Property ID *" : "Property ID"}
            </label>
            <Combobox
              ariaLabel={hasMultipleProperties ? "Property ID required" : "Property ID"}
              className="w-full"
              getOptionKey={(propertyId) => propertyId}
              getOptionLabel={(propertyId) => propertyId}
              id="import-property-id"
              inputValue={selectedPropertyId}
              onInputValueChange={setSelectedPropertyId}
              onSelectOption={setSelectedPropertyId}
              options={propertyIds}
              placeholder="Property ID"
            />
            {hasMultipleProperties && !selectedPropertyId.trim() ? (
              <p className="mt-1 text-xs text-red-600">Required for multi-property sites</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <AppLabel htmlFor="import-property-name">Property name *</AppLabel>
            <AppInput
              aria-invalid={!propertyNameOverride.trim()}
              id="import-property-name"
              value={propertyNameOverride}
              onChange={(event) => setPropertyNameOverride(event.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              Used as unit ACG name prefix
            </span>
          </div>
        </div>
        {phase === "parsing" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <AppSpinner />
            <span className="text-sm text-muted-foreground">Parsing CSV...</span>
          </div>
        )}

        {(parseResult?.errors?.length ?? 0) > 0 && (
          <AppAlert className="mb-2" variant="warning">
            <div className="text-sm font-semibold">
              {parseResult?.errors.length} row{parseResult?.errors.length !== 1 ? "s" : ""} skipped:
            </div>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {parseResult?.errors.map((e, i) => <li key={i} style={{ fontSize: "0.85em" }}>{e}</li>)}
            </ul>
          </AppAlert>
        )}

        {error && <div className="mb-3 text-destructive">{error}</div>}

        {phase === "previewing" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <AppSpinner />
            <span className="text-sm text-muted-foreground">Loading preview...</span>
          </div>
        )}

        {preview && phase !== "previewing" && (
          <>
            {preview.warning && (
              <AppAlert className="mb-2" variant="warning">{preview.warning}</AppAlert>
            )}
            {!hasChanges && !preview.warning && (
              <div className="mb-3 text-sm text-muted-foreground">
                No changes detected - all rows already match existing locks.
              </div>
            )}
            <ChangeList label="Locks" items={preview.locks}
              renderItem={(lock: LockChangePreview, i) => (
                <li key={lock.systemLockId ?? i} style={{ marginBottom: 4 }}>
                  <ActionBadge action={lock.action} /> {lock.name}
                  {lock.changes?.length > 0 && (
                    <ul style={{ margin: "2px 0 0", paddingLeft: 20, fontSize: "0.85em" }}>
                      {lock.changes.map((c, j) => <li key={j}>{c}</li>)}
                    </ul>
                  )}
                </li>
              )} />
            <ChangeList label="Groups" items={preview.groups ?? []}
              renderItem={(group: GroupChangePreview, i) => (
                <li key={group.systemGroupId ?? i} style={{ marginBottom: 4 }}>
                  <ActionBadge action={group.action} /> {group.name}
                  {(group.locksAdded?.length > 0 || group.locksRemoved?.length > 0) && (
                    <ul style={{ margin: "2px 0 0", paddingLeft: 20, fontSize: "0.85em" }}>
                      {group.locksAdded?.map((l, j) => <li key={j}>+ {l}</li>)}
                      {group.locksRemoved?.map((l, j) => <li key={j}>- {l}</li>)}
                    </ul>
                  )}
                </li>
              )} />
            {(preview.locks.length > 0 || (preview.groups?.length ?? 0) > 0) && (preview.unitAcgMatches?.length ?? 0) > 0 && <AppSeparator className="mb-4" />}
            <ChangeList label="Unit ACG matches" items={preview.unitAcgMatches ?? []}
              renderItem={(match: UnitAcgMatchPreview, i) => (
                <li key={`${match.lockName}-${match.acgName}`} style={{ marginBottom: 4 }}>
                  <ActionBadge action="Added" /> {match.lockName}{" "}
                  <span className="ml-0.5 text-muted-foreground">-&gt; {match.acgName}</span>
                </li>
              )} />
          </>
        )}

        {phase === "done" && importResult && (
          <AppAlert className="mt-2" variant="success">
            <div className="text-sm font-semibold">Import complete</div>
            <div className="text-sm">
              Locks created: {importResult.locksCreated}
              {importResult.acgsCreated > 0 && ` \u00b7 ACGs created: ${importResult.acgsCreated}`}
              {importResult.locksAddedToAcgs > 0 && ` \u00b7 Unit links created: ${importResult.locksAddedToAcgs}`}
            </div>
          </AppAlert>
        )}
      <AppDialogFooter>
        <AppButton onClick={onClose} type="button" variant="outline">
          {phase === "done" ? "Close" : "Cancel"}
        </AppButton>
        {canAct && phase !== "done" && (
          <AppButton type="button" variant="outline" onClick={handlePreview}
            disabled={phase === "previewing" || phase === "importing"}>
            Preview Changes
          </AppButton>
        )}
        {canAct && phase !== "done" && (
          <AppButton type="button"
            variant={additive ? "default" : "destructive"}
            onClick={handleImport}
            disabled={phase === "importing"}>
            {phase === "importing" ? "Importing\u2026" : additive ? "Import" : "Replace"}
          </AppButton>
        )}
      </AppDialogFooter>
      </AppDialogContent>
    </AppDialog>

    {/* Replacement confirmation dialog */}
    {confirmOpen && (
      <AppDialog
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setConfirmOpen(false);
          }
        }}
      >
        <AppDialogContent showCloseButton={false} className="sm:max-w-xs">
          <AppDialogHeader>
            <AppDialogTitle>Confirm replacement import</AppDialogTitle>
            <AppDialogDescription>
              Locks currently on this site that are <strong>not in your CSV file</strong> will be
              removed. This cannot be undone without a fresh sync from the access control system.
            </AppDialogDescription>
          </AppDialogHeader>
          <AppDialogFooter>
            <AppButton onClick={() => setConfirmOpen(false)} type="button" variant="outline">
              Cancel
            </AppButton>
            <AppButton
              type="button"
              variant="destructive"
              onClick={() => { setConfirmOpen(false); executeImport(); }}
            >
              Confirm Replace
            </AppButton>
          </AppDialogFooter>
        </AppDialogContent>
      </AppDialog>
    )}
  </>
  );
};
